import {startTransition, useEffect, useRef, useState} from 'react';

const SPIDER_URL = process.env.REACT_APP_SPIDER_URL;
const PUBLIC_URL = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const NOC_FIXTURE_URL = `${PUBLIC_URL}/noc.json`;
const SSE_FLUSH_DELAY_MS = 200;

let NOC_SOURCE = 'sse';
// NOC_SOURCE = 'fixture'; // Comente esta linha para voltar a usar a URL do SSE.

const getCityKey = item => String(item.ibge);
const getNocKey = item => String(item.idCapturePlace ?? item.capturePlaceName);

const createSnapshot = (value, initialized = false) => {
    const events = Array.isArray(value) ? value : [];
    const cities = new Map();
    const indexes = new Map();

    events.forEach((item, index) => {
        const key = getCityKey(item);
        cities.set(key, item);
        indexes.set(key, index);
    });

    return {
        events,
        cities,
        indexes,
        initialized,
        updatedAt: new Date().toLocaleString(),
    };
};

const mergeNoc = (currentNoc, updatedNoc) => {
    if (!Array.isArray(currentNoc) || !Array.isArray(updatedNoc)) return updatedNoc;
    if (updatedNoc.length >= currentNoc.length) return updatedNoc;

    const indexes = new Map();
    const mergedNoc = currentNoc.slice();

    currentNoc.forEach((item, index) => {
        indexes.set(getNocKey(item), index);
    });

    for (const item of updatedNoc) {
        const key = getNocKey(item);
        const index = indexes.get(key);

        if (index === undefined) {
            indexes.set(key, mergedNoc.length);
            mergedNoc.push(item);
        } else {
            mergedNoc[index] = {...mergedNoc[index], ...item};
        }
    }

    return mergedNoc;
};

const mergeCity = (currentCity, updatedCity) => {
    if (!currentCity) return updatedCity;

    const hasNoc = Object.prototype.hasOwnProperty.call(updatedCity, 'noc');

    return {
        ...currentCity,
        ...updatedCity,
        noc: hasNoc ? mergeNoc(currentCity.noc, updatedCity.noc) : currentCity.noc,
    };
};

const applyCityUpdates = (snapshot, updates) => {
    const events = snapshot.events.slice();
    const cities = new Map(snapshot.cities);
    const indexes = new Map(snapshot.indexes);

    for (const update of updates) {
        const key = getCityKey(update);
        const city = mergeCity(cities.get(key), update);
        const index = indexes.get(key);

        cities.set(key, city);

        if (index === undefined) {
            indexes.set(key, events.length);
            events.push(city);
        } else {
            events[index] = city;
        }
    }

    return {
        events,
        cities,
        indexes,
        initialized: snapshot.initialized,
        updatedAt: new Date().toLocaleString(),
    };
};

const selectNoc = (snapshot, code) => {
    if (code === undefined) return snapshot.events;

    const cityData = snapshot.cities.get(String(code));
    return cityData ? cityData.noc : [];
};

const UseSse = (hash, code) => {
    const [noc, setNoc] = useState([]);
    const [lastUpdate, setLastUpdate] = useState(new Date().toLocaleString());
    const snapshotRef = useRef(createSnapshot([]));
    const codeRef = useRef(code);

    useEffect(() => {
        codeRef.current = code;
    }, [code]);

    useEffect(() => {
        let ignore = false;
        let flushTimeoutId;
        let pendingFullPayload;
        let hasInitialSnapshot = false;
        const pendingUpdates = new Map();
        const pendingUpdatesBeforeInit = new Map();

        const updateNoc = snapshot => {
            if (ignore) return;

            snapshotRef.current = snapshot;

            startTransition(() => {
                setNoc(selectNoc(snapshot, codeRef.current));
                setLastUpdate(snapshot.updatedAt);
            });
        };

        const queueUpdates = (target, value) => {
            const updates = Array.isArray(value) ? value : [];

            for (const item of updates) {
                if (!item || item.ibge === undefined) continue;
                target.set(getCityKey(item), item);
            }
        };

        const flushPendingEvents = () => {
            flushTimeoutId = undefined;

            let snapshot = snapshotRef.current;

            if (pendingFullPayload) {
                snapshot = createSnapshot(pendingFullPayload, true);
                hasInitialSnapshot = true;
                pendingFullPayload = undefined;

                for (const [key, value] of pendingUpdatesBeforeInit.entries()) {
                    pendingUpdates.set(key, value);
                }

                pendingUpdatesBeforeInit.clear();
            }

            if (hasInitialSnapshot && pendingUpdates.size > 0) {
                snapshot = applyCityUpdates(snapshot, Array.from(pendingUpdates.values()));
                pendingUpdates.clear();
            }

            if (snapshot !== snapshotRef.current) {
                updateNoc(snapshot);
            }
        };

        const scheduleFlush = () => {
            if (flushTimeoutId) return;
            flushTimeoutId = setTimeout(flushPendingEvents, SSE_FLUSH_DELAY_MS);
        };

        const queuePayload = events => {
            if (events.type === 'heartbeat') return;

            if (events.type === 'init') {
                pendingFullPayload = events.value;
            } else if (hasInitialSnapshot || pendingFullPayload) {
                queueUpdates(pendingUpdates, events.value);
            } else {
                queueUpdates(pendingUpdatesBeforeInit, events.value);
            }

            scheduleFlush();
        };

        if (NOC_SOURCE === 'fixture') {
            fetch(NOC_FIXTURE_URL)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`Error to load noc.json: ${response.status}`);
                    }

                    return response.json();
                })
                .then(data => updateNoc(createSnapshot(data.value, true)))
                .catch(e => {
                    console.error('Error to load events.');
                    console.error(e);
                });

            return () => {
                ignore = true;
                clearTimeout(flushTimeoutId);
            };
        }

        if (!hash) return;

        const eventSource = new EventSource(`${SPIDER_URL}/sse/cameras/subscribe/${hash}`);

        eventSource.onmessage = event => {
            const events = JSON.parse(event.data);
            queuePayload(events);
        };

        eventSource.onerror = e => {
            if (!ignore) {
                console.error('Error to load events.');
                console.error(e);
            }
        };

        return () => {
            ignore = true;
            clearTimeout(flushTimeoutId);
            eventSource.close();
        };

    }, [hash]);

    useEffect(() => {
        const snapshot = snapshotRef.current;

        if (code === undefined) {
            setNoc(snapshot.events);
        } else {
            const cityData = snapshot.cities.get(String(code));
            setNoc(cityData ? cityData.noc : []);
        }

    }, [code]);

    return {noc, lastUpdate}
};

export default UseSse;
